import React, { useMemo } from 'react';
import {
  Space,
  Card,
  Table,
  Tag,
  Typography,
  Button,
  Select,
  Input,
  Row,
  Col,
  Statistic,
  DatePicker,
  Empty,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ReloadOutlined,
  EyeOutlined,
  TeamOutlined,
  CloudDownloadOutlined,
  MobileOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  FunnelChart,
  Funnel,
  LabelList,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useMarketingAnalytics } from '../hooks/useMarketingAnalytics';
import {
  CampaignAnalyticsRow,
  CampaignDevice,
} from '../../api/services/marketingService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const ORANGE = '#fe5a1d';
const BLUE = '#1677ff';
const GREEN = '#52c41a';
const PURPLE = '#722ed1';
const IOS_COLOR = '#111318';
const ANDROID_COLOR = '#3ddc84';
const FUNNEL_COLORS = [ORANGE, BLUE, GREEN, PURPLE];

/** Localized short axis label, e.g. "Jul 2". */
function useDayFormatter() {
  const { i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? 'en' : 'sq';
  return useMemo(
    () =>
      (value: string, opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }) => {
        const d = new Date(value);
        return Number.isNaN(d.getTime())
          ? value
          : new Intl.DateTimeFormat(locale, opts).format(d);
      },
    [locale],
  );
}

const chartFrame = (hasData: boolean, node: React.ReactElement, empty: string) =>
  hasData ? node : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={empty} />;

const MarketingAnalytics: React.FC = () => {
  const { t } = useTranslation();
  const fmtDay = useDayFormatter();
  const {
    overview,
    rows,
    timeseries,
    campaigns,
    loading,
    dateRange,
    campaignId,
    device,
    country,
    setDateRange,
    setCampaignId,
    setDevice,
    setCountry,
    refetch,
  } = useMarketingAnalytics();

  // Tooltip that renders the full localized date as its title.
  interface TooltipEntry {
    dataKey?: string | number;
    name?: string;
    value?: number;
    color?: string;
  }
  const DateTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: TooltipEntry[];
    label?: string | number;
  }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '8px 12px', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          {typeof label === 'string' ? fmtDay(label, { year: 'numeric', month: 'short', day: 'numeric' }) : label}
        </div>
        {payload.map((p) => (
          <div key={String(p.dataKey)} style={{ color: p.color, fontSize: 12 }}>
            {p.name}: <strong>{p.value}</strong>
          </div>
        ))}
      </div>
    );
  };

  // ---- derived chart data ----
  const downloadsData = useMemo(() => {
    const map = new Map<string, { date: string; IOS: number; ANDROID: number }>();
    (timeseries?.downloadsByPlatform ?? []).forEach((d) => {
      const entry = map.get(d.date) ?? { date: d.date, IOS: 0, ANDROID: 0 };
      if (d.platform === 'IOS') entry.IOS = d.count;
      else if (d.platform === 'ANDROID') entry.ANDROID = d.count;
      map.set(d.date, entry);
    });
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [timeseries]);

  const visitsRedirectsData = useMemo(() => {
    const map = new Map<string, { date: string; visits: number; redirects: number }>();
    (timeseries?.dailyVisits ?? []).forEach((d) => {
      const e = map.get(d.date) ?? { date: d.date, visits: 0, redirects: 0 };
      e.visits = d.count;
      map.set(d.date, e);
    });
    downloadsData.forEach((d) => {
      const e = map.get(d.date) ?? { date: d.date, visits: 0, redirects: 0 };
      e.redirects = d.IOS + d.ANDROID;
      map.set(d.date, e);
    });
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [timeseries, downloadsData]);

  const trendData = useMemo(() => {
    const map = new Map<string, { date: string; installs: number; registrations: number }>();
    (timeseries?.dailyInstalls ?? []).forEach((d) => {
      const e = map.get(d.date) ?? { date: d.date, installs: 0, registrations: 0 };
      e.installs = d.count;
      map.set(d.date, e);
    });
    (timeseries?.dailyRegistrations ?? []).forEach((d) => {
      const e = map.get(d.date) ?? { date: d.date, installs: 0, registrations: 0 };
      e.registrations = d.count;
      map.set(d.date, e);
    });
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [timeseries]);

  const topInfluencers = useMemo(
    () => rows.filter((r) => r.visits > 0).slice(0, 8).map((r) => ({ name: r.name, visits: r.visits, installs: r.installs })),
    [rows],
  );

  const funnelData = useMemo(
    () => [
      { name: t('admin.marketing.stat_visits'), value: overview?.totalVisits ?? 0, fill: FUNNEL_COLORS[0] },
      { name: t('admin.marketing.stat_redirects'), value: overview?.totalRedirects ?? 0, fill: FUNNEL_COLORS[1] },
      { name: t('admin.marketing.stat_installs'), value: overview?.totalInstalls ?? 0, fill: FUNNEL_COLORS[2] },
      { name: t('admin.marketing.stat_registrations'), value: overview?.totalRegistrations ?? 0, fill: FUNNEL_COLORS[3] },
    ],
    [overview, t],
  );

  const platformData = useMemo(
    () => [
      { name: 'iOS', value: overview?.iosClicks ?? 0, fill: IOS_COLOR },
      { name: 'Android', value: overview?.androidClicks ?? 0, fill: ANDROID_COLOR },
    ],
    [overview],
  );

  const hasFunnel = (overview?.totalVisits ?? 0) > 0;
  const hasPlatform = (overview?.iosClicks ?? 0) + (overview?.androidClicks ?? 0) > 0;

  const columns: ColumnsType<CampaignAnalyticsRow> = [
    {
      title: t('admin.marketing.col_influencer'),
      dataIndex: 'name',
      key: 'name',
      render: (v: string, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>/{r.slug}</Text>
        </Space>
      ),
    },
    { title: t('admin.marketing.col_visits'), dataIndex: 'visits', key: 'visits', align: 'right', sorter: (a, b) => a.visits - b.visits },
    { title: t('admin.marketing.col_ios'), dataIndex: 'iosClicks', key: 'iosClicks', align: 'right' },
    { title: t('admin.marketing.col_android'), dataIndex: 'androidClicks', key: 'androidClicks', align: 'right' },
    { title: t('admin.marketing.col_installs'), dataIndex: 'installs', key: 'installs', align: 'right', sorter: (a, b) => a.installs - b.installs },
    { title: t('admin.marketing.col_registrations'), dataIndex: 'registrations', key: 'registrations', align: 'right' },
    {
      title: t('admin.marketing.col_conversion'),
      dataIndex: 'conversion',
      key: 'conversion',
      align: 'right',
      sorter: (a, b) => a.conversion - b.conversion,
      render: (v: number) => <Tag color={v >= 10 ? 'green' : v > 0 ? 'orange' : 'default'}>{v}%</Tag>,
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Header + filters */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>{t('admin.marketing.analytics_title')}</Title>
          <Text type="secondary">{t('admin.marketing.analytics_subtitle')}</Text>
        </div>
        <Space wrap>
          <RangePicker
            className="admin-filter-picker"
            value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setDateRange([dates[0].startOf('day').toISOString(), dates[1].endOf('day').toISOString()]);
              } else {
                setDateRange(null);
              }
            }}
          />
          <Select allowClear placeholder={t('admin.marketing.filter_influencer')} style={{ width: 160 }} value={campaignId} onChange={(v) => setCampaignId(v)} options={campaigns.map((c) => ({ value: c.id, label: c.name }))} />
          <Select
            allowClear
            placeholder={t('admin.marketing.filter_device')}
            style={{ width: 130 }}
            value={device}
            onChange={(v) => setDevice(v as CampaignDevice | undefined)}
            options={[
              { value: 'MOBILE', label: t('admin.marketing.device_mobile') },
              { value: 'TABLET', label: t('admin.marketing.device_tablet') },
              { value: 'DESKTOP', label: t('admin.marketing.device_desktop') },
            ]}
          />
          <Input allowClear placeholder={t('admin.marketing.filter_country')} style={{ width: 110 }} maxLength={2} value={country} onChange={(e) => setCountry(e.target.value || undefined)} />
          <Button type="primary" className="ant-btn-primary admin-filter-btn admin-filter-btn-primary" icon={<ReloadOutlined />} onClick={refetch} loading={loading}>
            {t('admin.marketing.btn_refresh')}
          </Button>
        </Space>
      </div>

      {/* Overview cards */}
      <Row gutter={[16, 16]} align="stretch">
        <Col xs={12} md={5}>
          <Card bordered={false} className="admin-stat-card" style={{ height: '100%' }}>
            <Statistic title={t('admin.marketing.stat_visits')} value={overview?.totalVisits ?? 0} prefix={<EyeOutlined />} valueStyle={{ color: ORANGE }} />
            <Text type="secondary" style={{ fontSize: 12 }}>{t('admin.marketing.stat_unique', { count: overview?.uniqueVisitors ?? 0 })}</Text>
          </Card>
        </Col>
        <Col xs={12} md={5}>
          <Card bordered={false} className="admin-stat-card" style={{ height: '100%' }}>
            <Statistic title={t('admin.marketing.stat_redirects')} value={overview?.totalRedirects ?? 0} prefix={<CloudDownloadOutlined />} valueStyle={{ color: BLUE }} />
            <Text type="secondary" style={{ fontSize: 12 }}>iOS {overview?.iosClicks ?? 0} · Android {overview?.androidClicks ?? 0}</Text>
          </Card>
        </Col>
        <Col xs={12} md={5}>
          <Card bordered={false} className="admin-stat-card" style={{ height: '100%' }}>
            <Statistic title={t('admin.marketing.stat_installs')} value={overview?.totalInstalls ?? 0} prefix={<MobileOutlined />} valueStyle={{ color: GREEN }} />
            <Text type="secondary" style={{ fontSize: 12 }}>{t('admin.marketing.stat_installs_sub', { rate: overview?.conversion.redirectToInstall ?? 0 })}</Text>
          </Card>
        </Col>
        <Col xs={12} md={5}>
          <Card bordered={false} className="admin-stat-card" style={{ height: '100%' }}>
            <Statistic title={t('admin.marketing.stat_registrations')} value={overview?.totalRegistrations ?? 0} prefix={<TeamOutlined />} valueStyle={{ color: PURPLE }} />
            <Text type="secondary" style={{ fontSize: 12 }}>{t('admin.marketing.stat_registrations_sub', { rate: overview?.conversion.installToRegistration ?? 0 })}</Text>
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card bordered={false} className="admin-stat-card" style={{ height: '100%' }}>
            <Statistic title={t('admin.marketing.stat_conversion')} value={overview?.conversion.visitToInstall ?? 0} suffix="%" prefix={<RiseOutlined />} valueStyle={{ color: ORANGE }} />
            <Text type="secondary" style={{ fontSize: 12 }}>{t('admin.marketing.stat_conversion_sub')}</Text>
          </Card>
        </Col>
      </Row>

      {/* Funnel + platform split */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={15}>
          <Card bordered={false} className="admin-table-card" title={<Text strong>{t('admin.marketing.chart_funnel')}</Text>}>
            {chartFrame(
              hasFunnel,
              <ResponsiveContainer width="100%" height={280}>
                <FunnelChart>
                  <Tooltip />
                  <Funnel dataKey="value" data={funnelData} isAnimationActive lastShapeType="rectangle">
                    <LabelList position="right" dataKey="name" stroke="none" fill="#555" fontSize={12} />
                    <LabelList position="inside" dataKey="value" stroke="none" fill="#fff" fontSize={14} fontWeight={700} />
                  </Funnel>
                </FunnelChart>
              </ResponsiveContainer>,
              t('admin.marketing.empty_no_data'),
            )}
          </Card>
        </Col>
        <Col xs={24} lg={9}>
          <Card bordered={false} className="admin-table-card" title={<Text strong>{t('admin.marketing.chart_platform_split')}</Text>}>
            {chartFrame(
              hasPlatform,
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={platformData} dataKey="value" nameKey="name" innerRadius={62} outerRadius={95} paddingAngle={2} stroke="none">
                    {platformData.map((e) => (
                      <Cell key={e.name} fill={e.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>,
              t('admin.marketing.empty_no_data'),
            )}
          </Card>
        </Col>
      </Row>

      {/* Time series */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card bordered={false} className="admin-table-card" title={<Text strong>{t('admin.marketing.chart_visits_redirects')}</Text>}>
            {chartFrame(
              visitsRedirectsData.length > 0,
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={visitsRedirectsData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                  <defs>
                    <linearGradient id="visitsFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={ORANGE} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={ORANGE} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(v) => fmtDay(v)} fontSize={11} tickMargin={8} minTickGap={20} />
                  <YAxis allowDecimals={false} fontSize={11} width={36} />
                  <Tooltip content={<DateTooltip />} />
                  <Legend />
                  <Area type="monotone" dataKey="visits" stroke={ORANGE} strokeWidth={2} fill="url(#visitsFill)" name={t('admin.marketing.label_visits')} />
                  <Line type="monotone" dataKey="redirects" stroke={BLUE} strokeWidth={2} dot={false} name={t('admin.marketing.label_redirects')} />
                </ComposedChart>
              </ResponsiveContainer>,
              t('admin.marketing.empty_no_data'),
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card bordered={false} className="admin-table-card" title={<Text strong>{t('admin.marketing.chart_downloads_platform')}</Text>}>
            {chartFrame(
              downloadsData.length > 0,
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={downloadsData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }} barCategoryGap="25%">
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(v) => fmtDay(v)} fontSize={11} tickMargin={8} minTickGap={20} />
                  <YAxis allowDecimals={false} fontSize={11} width={36} />
                  <Tooltip content={<DateTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                  <Legend />
                  <Bar dataKey="IOS" stackId="p" fill={IOS_COLOR} name="iOS" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="ANDROID" stackId="p" fill={ANDROID_COLOR} name="Android" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>,
              t('admin.marketing.empty_no_data'),
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card bordered={false} className="admin-table-card" title={<Text strong>{t('admin.marketing.chart_top_influencers')}</Text>}>
            {chartFrame(
              topInfluencers.length > 0,
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={topInfluencers} layout="vertical" margin={{ top: 8, right: 24, left: 12, bottom: 0 }} barCategoryGap="25%">
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} horizontal={false} />
                  <XAxis type="number" allowDecimals={false} fontSize={11} />
                  <YAxis type="category" dataKey="name" width={100} fontSize={11} />
                  <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                  <Legend />
                  <Bar dataKey="visits" fill={ORANGE} name={t('admin.marketing.stat_visits')} radius={[0, 4, 4, 0]}>
                    <LabelList dataKey="visits" position="right" fontSize={11} />
                  </Bar>
                  <Bar dataKey="installs" fill={GREEN} name={t('admin.marketing.stat_installs')} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>,
              t('admin.marketing.empty_no_data'),
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card bordered={false} className="admin-table-card" title={<Text strong>{t('admin.marketing.chart_install_reg_trends')}</Text>}>
            {chartFrame(
              trendData.length > 0,
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={trendData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(v) => fmtDay(v)} fontSize={11} tickMargin={8} minTickGap={20} />
                  <YAxis allowDecimals={false} fontSize={11} width={36} />
                  <Tooltip content={<DateTooltip />} />
                  <Legend />
                  <Line type="monotone" dataKey="installs" stroke={GREEN} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} name={t('admin.marketing.stat_installs')} />
                  <Line type="monotone" dataKey="registrations" stroke={PURPLE} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} name={t('admin.marketing.stat_registrations')} />
                </LineChart>
              </ResponsiveContainer>,
              t('admin.marketing.empty_no_data'),
            )}
          </Card>
        </Col>
      </Row>

      {/* Per-influencer table */}
      <Card bordered={false} className="admin-table-card" title={<Text strong>{t('admin.marketing.section_per_influencer')}</Text>}>
        <Table<CampaignAnalyticsRow>
          rowKey="campaignId"
          loading={loading}
          dataSource={rows}
          columns={columns}
          pagination={false}
          size="small"
          scroll={{ x: 700 }}
          locale={{ emptyText: t('admin.marketing.empty_no_data') }}
        />
      </Card>
    </Space>
  );
};

export default MarketingAnalytics;
